import type {
  Adsr,
  DrumKit,
  DrumKitMode,
  DrumPad,
  ExternalMidiPatch,
  Instrument,
  InstrumentSlot,
  Sf2Patch,
  SynthPreset,
  WavetablePreset,
} from "../types";
import { DEFAULT_INSTRUMENT_SLOT_COUNT, clampInstrumentSlotCount } from "../types";
import { clonePreset, defaultPresetForSlot, defaultSlotName } from "./defaultPresets";
import {
  sf2Instrument,
  normalizeSf2Patch,
  sf2PatchDisplayName,
} from "../sf2/patch";
import {
  cloneWavetablePreset,
  createDefaultWavetablePreset,
  isWavetableInstrument,
  normalizeWavetablePreset,
  wavetableInstrument,
} from "../wavetable/preset";
import {
  cloneExternalMidiPatch,
  createDefaultExternalMidiPatch,
  externalMidiInstrument,
  isExternalMidiInstrument,
  normalizeExternalMidiPatch,
} from "../externalMidi/patch";
import {
  cloneSamplerPreset,
  createDefaultSamplerPreset,
  isSamplerInstrument,
  normalizeSamplerPreset,
  samplerInstrument,
} from "../sampler/preset";

export { isWavetableInstrument, wavetableInstrument, normalizeWavetablePreset, createDefaultWavetablePreset };
export {
  isSamplerInstrument,
  samplerInstrument,
  normalizeSamplerPreset,
  createDefaultSamplerPreset,
  cloneSamplerPreset,
};

export const DRUM_PAD_COUNT = 64;
export const DEFAULT_DRUM_BASE_NOTE = 60; // C4 — Studio Kit layout
/** GM percussion Acoustic Bass Drum — start of the contiguous 16-pad GM subset. */
export const GM_DRUM_BASE_NOTE = 36;

export type { DrumKitMode };

export type InstrumentKind = Instrument["kind"];

export function isSynthInstrument(
  instrument: Instrument,
): instrument is { kind: "synth"; preset: SynthPreset } {
  return instrument.kind === "synth";
}

export function isDrumsInstrument(
  instrument: Instrument,
): instrument is { kind: "drums"; kit: DrumKit } {
  return instrument.kind === "drums";
}

export function isWavetableKind(
  instrument: Instrument,
): instrument is { kind: "wavetable"; preset: WavetablePreset } {
  return instrument.kind === "wavetable";
}

export {
  isSf2Instrument,
  isDrumInstrument,
  sf2Instrument,
  normalizeSf2Patch,
  cloneSf2Patch,
  createDefaultSf2Patch,
  createDefaultDrumSf2Patch,
  createDistortionGuitarSf2Patch,
  createElectricGuitarSf2Patch,
  gmProgramForSf2Patch,
  isDistortionGuitarGm,
  isElectricGuitarGm,
  sf2PatchForGmMelodic,
} from "../sf2/patch";

export {
  createDefaultExternalMidiPatch,
  normalizeExternalMidiPatch,
  externalMidiInstrument,
  isExternalMidiInstrument,
  cloneExternalMidiPatch,
} from "../externalMidi/patch";

export function synthInstrument(preset: SynthPreset): Instrument {
  return { kind: "synth", preset: clonePreset(preset) };
}

export function drumsInstrument(kit: DrumKit): Instrument {
  return { kind: "drums", kit: cloneDrumKit(kit) };
}

export function cloneDrumPad(pad: DrumPad): DrumPad {
  return {
    ...pad,
    ...(pad.region ? { region: { ...pad.region } } : {}),
    ...(pad.ampEnv ? { ampEnv: { ...pad.ampEnv } } : {}),
  };
}

export function cloneDrumKit(kit: DrumKit): DrumKit {
  return {
    name: kit.name,
    baseNote: kit.baseNote,
    mode: kit.mode === "sample" ? "sample" : "synth",
    pads: kit.pads.map(cloneDrumPad),
  };
}

export function cloneInstrument(instrument: Instrument): Instrument {
  if (instrument.kind === "synth") {
    return synthInstrument(instrument.preset);
  }
  if (instrument.kind === "drums") {
    return drumsInstrument(instrument.kit);
  }
  if (instrument.kind === "wavetable") {
    return wavetableInstrument(instrument.preset);
  }
  if (instrument.kind === "sampler") {
    return samplerInstrument(instrument.preset);
  }
  if (instrument.kind === "externalMidi") {
    return externalMidiInstrument(instrument.patch);
  }
  return sf2Instrument(instrument.patch);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function defaultPad(name: string, partial: Partial<DrumPad> = {}): DrumPad {
  return {
    name,
    level: 0.85,
    pitchHz: 120,
    pitchEnv: 1.2,
    decayMs: 180,
    toneLevel: 0.7,
    noiseLevel: 0.15,
    clickLevel: 0.25,
    filterHz: 6000,
    ...partial,
  };
}

function mutedPad(name: string): DrumPad {
  return defaultPad(name, {
    level: 0,
    toneLevel: 0,
    noiseLevel: 0,
    clickLevel: 0,
    decayMs: 40,
  });
}

/**
 * Factory “Studio Kit” — kick / snare / hats / clap on the first pads;
 * remaining pads are quiet fillers the user can edit.
 * Chromatic from C4 (MIDI 60) — tracker / arm-friendly, not GM channel 10.
 */
export function createDefaultDrumKit(): DrumKit {
  const pads: DrumPad[] = [
    defaultPad("Kick", {
      pitchHz: 58,
      pitchEnv: 2.4,
      decayMs: 220,
      toneLevel: 0.95,
      noiseLevel: 0.05,
      clickLevel: 0.35,
      filterHz: 2800,
      level: 1,
    }),
    defaultPad("Snare", {
      pitchHz: 180,
      pitchEnv: 0.6,
      decayMs: 160,
      toneLevel: 0.35,
      noiseLevel: 0.85,
      clickLevel: 0.45,
      filterHz: 9000,
      level: 0.9,
    }),
    defaultPad("Closed Hat", {
      pitchHz: 420,
      pitchEnv: 0.1,
      decayMs: 55,
      toneLevel: 0.05,
      noiseLevel: 1,
      clickLevel: 0.2,
      filterHz: 14000,
      level: 0.7,
    }),
    defaultPad("Clap", {
      pitchHz: 220,
      pitchEnv: 0.2,
      decayMs: 140,
      toneLevel: 0.1,
      noiseLevel: 0.95,
      clickLevel: 0.55,
      filterHz: 8000,
      level: 0.85,
    }),
    defaultPad("Open Hat", {
      pitchHz: 380,
      pitchEnv: 0.05,
      decayMs: 320,
      toneLevel: 0.05,
      noiseLevel: 1,
      clickLevel: 0.15,
      filterHz: 12000,
      level: 0.65,
    }),
    defaultPad("Tom Lo", {
      pitchHz: 95,
      pitchEnv: 1.4,
      decayMs: 280,
      toneLevel: 0.9,
      noiseLevel: 0.1,
      clickLevel: 0.2,
      filterHz: 4000,
      level: 0.85,
    }),
    defaultPad("Tom Hi", {
      pitchHz: 160,
      pitchEnv: 1.1,
      decayMs: 220,
      toneLevel: 0.9,
      noiseLevel: 0.1,
      clickLevel: 0.2,
      filterHz: 5000,
      level: 0.8,
    }),
    defaultPad("Rim", {
      pitchHz: 900,
      pitchEnv: 0.4,
      decayMs: 50,
      toneLevel: 0.5,
      noiseLevel: 0.4,
      clickLevel: 0.7,
      filterHz: 10000,
      level: 0.7,
    }),
    mutedPad("Pad 9"),
    mutedPad("Pad 10"),
    mutedPad("Pad 11"),
    mutedPad("Pad 12"),
    mutedPad("Pad 13"),
    mutedPad("Pad 14"),
    mutedPad("Pad 15"),
    mutedPad("Pad 16"),
  ];
  while (pads.length < DRUM_PAD_COUNT) {
    pads.push(mutedPad(`Pad ${pads.length + 1}`));
  }
  return {
    name: "Studio Kit",
    baseNote: DEFAULT_DRUM_BASE_NOTE,
    mode: "synth",
    pads,
  };
}

/**
 * Contiguous GM percussion subset MIDI 36–51 (Acoustic Bass Drum → Ride Cymbal 1).
 * Used for MIDI channel-10 import so notes land on real pads instead of C4 studio layout.
 */
export function createGmDrumKit(): DrumKit {
  const kick = (name: string, partial: Partial<DrumPad> = {}): DrumPad =>
    defaultPad(name, {
      pitchHz: 55,
      pitchEnv: 2.6,
      decayMs: 240,
      toneLevel: 0.95,
      noiseLevel: 0.04,
      clickLevel: 0.4,
      filterHz: 2600,
      level: 1,
      ...partial,
    });
  const snare = (name: string, partial: Partial<DrumPad> = {}): DrumPad =>
    defaultPad(name, {
      pitchHz: 185,
      pitchEnv: 0.55,
      decayMs: 155,
      toneLevel: 0.3,
      noiseLevel: 0.9,
      clickLevel: 0.5,
      filterHz: 9500,
      level: 0.92,
      ...partial,
    });
  const hat = (name: string, partial: Partial<DrumPad> = {}): DrumPad =>
    defaultPad(name, {
      pitchHz: 400,
      pitchEnv: 0.08,
      decayMs: 50,
      toneLevel: 0.04,
      noiseLevel: 1,
      clickLevel: 0.18,
      filterHz: 14500,
      level: 0.68,
      ...partial,
    });
  const tom = (name: string, hz: number, partial: Partial<DrumPad> = {}): DrumPad =>
    defaultPad(name, {
      pitchHz: hz,
      pitchEnv: 1.35,
      decayMs: 260,
      toneLevel: 0.9,
      noiseLevel: 0.08,
      clickLevel: 0.18,
      filterHz: 4200,
      level: 0.85,
      ...partial,
    });
  const cymbal = (name: string, partial: Partial<DrumPad> = {}): DrumPad =>
    defaultPad(name, {
      pitchHz: 280,
      pitchEnv: 0.05,
      decayMs: 900,
      toneLevel: 0.08,
      noiseLevel: 1,
      clickLevel: 0.35,
      filterHz: 11000,
      level: 0.75,
      ...partial,
    });

  const pads: DrumPad[] = [
    kick("Kick"), // 36 Acoustic Bass Drum
    defaultPad("Side Stick", {
      pitchHz: 1100,
      pitchEnv: 0.25,
      decayMs: 45,
      toneLevel: 0.45,
      noiseLevel: 0.35,
      clickLevel: 0.85,
      filterHz: 12000,
      level: 0.7,
    }), // 37
    snare("Snare"), // 38 Acoustic Snare
    defaultPad("Clap", {
      pitchHz: 220,
      pitchEnv: 0.2,
      decayMs: 140,
      toneLevel: 0.1,
      noiseLevel: 0.95,
      clickLevel: 0.55,
      filterHz: 8000,
      level: 0.85,
    }), // 39
    snare("E. Snare", { pitchHz: 210, noiseLevel: 0.75, toneLevel: 0.45, decayMs: 130 }), // 40
    tom("Floor Tom Lo", 78), // 41
    hat("Closed Hat"), // 42
    tom("Floor Tom Hi", 95, { decayMs: 240 }), // 43
    hat("Pedal Hat", { decayMs: 70, level: 0.55, filterHz: 10000 }), // 44
    tom("Tom Lo", 110), // 45
    hat("Open Hat", { decayMs: 340, level: 0.62, filterHz: 12500, clickLevel: 0.12 }), // 46
    tom("Tom Mid", 135, { decayMs: 230 }), // 47
    tom("Tom Hi Mid", 155, { decayMs: 210 }), // 48
    cymbal("Crash", { decayMs: 1100, level: 0.8, clickLevel: 0.45 }), // 49
    tom("Tom Hi", 185, { decayMs: 190, filterHz: 5200 }), // 50
    cymbal("Ride", { pitchHz: 320, decayMs: 750, level: 0.7, clickLevel: 0.25, filterHz: 9000 }), // 51
  ];
  while (pads.length < DRUM_PAD_COUNT) {
    pads.push(mutedPad(`Pad ${pads.length + 1}`));
  }

  return {
    name: "GM Drums",
    baseNote: GM_DRUM_BASE_NOTE,
    mode: "synth",
    pads,
  };
}

/**
 * Map a MIDI note onto a kit's chromatic pad range.
 * GM aliases (e.g. 35 → 36) land on the intended pad; outliers clamp into range.
 */
export function mapMidiNoteToDrumKitNote(kit: DrumKit, midi: number): number {
  let note = Math.max(0, Math.min(127, Math.round(midi)));
  const alias = GM_DRUM_NOTE_ALIASES[note];
  if (alias !== undefined) {
    note = alias;
  }
  const lo = kit.baseNote;
  const hi = kit.baseNote + DRUM_PAD_COUNT - 1;
  return Math.min(hi, Math.max(lo, note));
}

/** Common GM percussion notes outside the 36–51 window → nearest kit pad. */
const GM_DRUM_NOTE_ALIASES: Record<number, number> = {
  35: 36, // Acoustic Bass Drum
  52: 51, // Chinese Cymbal → Ride
  53: 51, // Ride Bell → Ride
  54: 39, // Tambourine → Clap
  55: 49, // Splash → Crash
  56: 37, // Cowbell → Side Stick
  57: 49, // Crash Cymbal 2 → Crash
  58: 37, // Vibraslap → Side Stick
  59: 51, // Ride Cymbal 2 → Ride
  70: 39, // Maracas → Clap
  75: 37, // Claves → Side Stick
  76: 37, // Hi Wood Block → Side Stick
  77: 37, // Low Wood Block → Side Stick
};

/**
 * Sample-mode default amp: near-zero A/D/R, full sustain.
 * Ooneshots play the WAV unshaped until EOF / --- / Cxx.
 */
export const DEFAULT_DRUM_SAMPLE_AMP_ENV: Adsr = {
  a: 0.001,
  d: 0.01,
  s: 1,
  r: 0.02,
};

function normalizeDrumAmpEnv(raw: Partial<Adsr> | undefined): Adsr {
  const fallback = DEFAULT_DRUM_SAMPLE_AMP_ENV;
  return {
    a: clamp(Number(raw?.a ?? fallback.a), 0, 8),
    d: clamp(Number(raw?.d ?? fallback.d), 0, 8),
    s: clamp(Number(raw?.s ?? fallback.s), 0, 1),
    r: clamp(Number(raw?.r ?? fallback.r), 0, 8),
  };
}

export function normalizeDrumPad(raw: Partial<DrumPad> | undefined, fallbackName: string): DrumPad {
  const base = defaultPad(fallbackName);
  if (!raw || typeof raw !== "object") {
    return { ...base, ampEnv: { ...DEFAULT_DRUM_SAMPLE_AMP_ENV } };
  }
  const pad: DrumPad = {
    name: typeof raw.name === "string" && raw.name.trim() ? raw.name.trim().slice(0, 24) : fallbackName,
    level: clamp(Number(raw.level ?? base.level), 0, 1),
    pitchHz: clamp(Number(raw.pitchHz ?? base.pitchHz), 20, 8000),
    pitchEnv: clamp(Number(raw.pitchEnv ?? base.pitchEnv), 0, 4),
    decayMs: clamp(Number(raw.decayMs ?? base.decayMs), 10, 2000),
    toneLevel: clamp(Number(raw.toneLevel ?? base.toneLevel), 0, 1),
    noiseLevel: clamp(Number(raw.noiseLevel ?? base.noiseLevel), 0, 1),
    clickLevel: clamp(Number(raw.clickLevel ?? base.clickLevel), 0, 1),
    filterHz: clamp(Number(raw.filterHz ?? base.filterHz), 200, 18000),
    ampEnv: normalizeDrumAmpEnv(raw.ampEnv),
  };
  if (typeof raw.sampleId === "string" && raw.sampleId.trim()) {
    pad.sampleId = raw.sampleId.trim();
  }
  if (raw.pitchSemis != null && Number.isFinite(Number(raw.pitchSemis))) {
    pad.pitchSemis = clamp(Number(raw.pitchSemis), -48, 48);
  }
  if (
    raw.region
    && typeof raw.region === "object"
    && Number.isFinite(Number(raw.region.start))
    && Number.isFinite(Number(raw.region.end))
  ) {
    const start = Math.max(0, Math.floor(Number(raw.region.start)));
    const end = Math.max(start + 1, Math.floor(Number(raw.region.end)));
    pad.region = { start, end };
  }
  return pad;
}

export function normalizeDrumKitMode(raw: unknown): DrumKitMode {
  return raw === "sample" ? "sample" : "synth";
}

export function normalizeDrumKit(raw: Partial<DrumKit> | undefined): DrumKit {
  const factory = createDefaultDrumKit();
  if (!raw || typeof raw !== "object") {
    return factory;
  }
  const padsIn = Array.isArray(raw.pads) ? raw.pads : [];
  const pads = Array.from({ length: DRUM_PAD_COUNT }, (_, i) =>
    normalizeDrumPad(
      padsIn[i] as Partial<DrumPad> | undefined,
      factory.pads[i]?.name ?? `Pad ${i + 1}`,
    ),
  );
  // Keep GM-style base notes usable with 64 pads (base + 63 ≤ 127).
  const maxBase = Math.max(0, 127 - (DRUM_PAD_COUNT - 1));
  return {
    name: typeof raw.name === "string" && raw.name.trim()
      ? raw.name.trim().slice(0, 48)
      : factory.name,
    baseNote: clamp(Math.round(Number(raw.baseNote ?? factory.baseNote)), 0, maxBase),
    mode: normalizeDrumKitMode(raw.mode),
    pads,
  };
}

/** Sample ids referenced by a sample-mode drum kit (for OPFS / engine load). */
export function drumKitSampleIds(kit: DrumKit): string[] {
  if (normalizeDrumKitMode(kit.mode) !== "sample") {
    return [];
  }
  const ids = new Set<string>();
  for (const pad of kit.pads) {
    if (typeof pad.sampleId === "string" && pad.sampleId.trim()) {
      ids.add(pad.sampleId.trim());
    }
  }
  return [...ids];
}

/** True when a pad name looks like an auto-generated filler (“Pad 12”). */
export function isGenericDrumPadName(name: string): boolean {
  return /^Pad\s+\d+$/i.test(name.trim());
}

export function normalizeInstrument(raw: unknown, slot: InstrumentSlot = 0): Instrument {
  if (raw && typeof raw === "object" && (raw as Instrument).kind === "drums") {
    return drumsInstrument(normalizeDrumKit((raw as { kit?: Partial<DrumKit> }).kit));
  }
  if (raw && typeof raw === "object" && (raw as Instrument).kind === "sf2") {
    return sf2Instrument(normalizeSf2Patch((raw as { patch?: Partial<Sf2Patch> }).patch));
  }
  if (raw && typeof raw === "object" && (raw as Instrument).kind === "wavetable") {
    const preset = (raw as { preset?: unknown }).preset;
    return wavetableInstrument(normalizeWavetablePreset(preset));
  }
  if (raw && typeof raw === "object" && (raw as Instrument).kind === "sampler") {
    const preset = (raw as { preset?: unknown }).preset;
    return samplerInstrument(normalizeSamplerPreset(preset));
  }
  if (raw && typeof raw === "object" && (raw as Instrument).kind === "externalMidi") {
    return externalMidiInstrument((raw as { patch?: Partial<ExternalMidiPatch> }).patch);
  }
  if (raw && typeof raw === "object" && (raw as Instrument).kind === "synth") {
    const preset = (raw as { preset?: SynthPreset }).preset;
    return synthInstrument(preset ?? defaultPresetForSlot(slot));
  }
  if (raw && typeof raw === "object" && "osc1" in (raw as object) && "voices" in (raw as object)) {
    return wavetableInstrument(normalizeWavetablePreset(raw));
  }
  if (raw && typeof raw === "object" && "osc1" in (raw as object)) {
    return synthInstrument(raw as SynthPreset);
  }
  return synthInstrument(defaultPresetForSlot(slot));
}

/** Resolve pad index for a MIDI note, or undefined if out of kit range / disabled. */
export function drumPadIndexForNote(kit: DrumKit, midi: number): number | undefined {
  const index = Math.round(midi) - kit.baseNote;
  if (index < 0 || index >= kit.pads.length) {
    return undefined;
  }
  const pad = kit.pads[index];
  if (!pad || pad.level <= 0) {
    return undefined;
  }
  if (normalizeDrumKitMode(kit.mode) === "sample" && !pad.sampleId) {
    return undefined;
  }
  return index;
}

export function createDefaultInstruments(
  count: number = DEFAULT_INSTRUMENT_SLOT_COUNT,
): Instrument[] {
  const n = clampInstrumentSlotCount(count);
  return Array.from({ length: n }, (_, i) => synthInstrument(defaultPresetForSlot(i)));
}

export function instrumentsFromPresets(
  presets: SynthPreset[],
  count: number = presets.length || DEFAULT_INSTRUMENT_SLOT_COUNT,
): Instrument[] {
  const n = clampInstrumentSlotCount(count);
  return Array.from({ length: n }, (_, i) => {
    const preset = presets[i] ?? defaultPresetForSlot(i);
    return synthInstrument(preset);
  });
}

/**
 * Synth-preset mirror for UI/bank code that still expects SynthPreset[].
 * Drum slots fall back to the factory synth default for that slot index.
 */
export function presetsFromInstruments(instruments: Instrument[]): SynthPreset[] {
  return Array.from({ length: instruments.length }, (_, i) => {
    const instrument = instruments[i];
    if (instrument && isSynthInstrument(instrument)) {
      return clonePreset(instrument.preset);
    }
    return defaultPresetForSlot(i);
  });
}

export function instrumentDisplayName(
  instrument: Instrument | undefined,
  slot: InstrumentSlot,
): string {
  if (!instrument) {
    return defaultSlotName(slot);
  }
  if (instrument.kind === "drums") {
    return instrument.kit.name;
  }
  if (instrument.kind === "sf2") {
    return sf2PatchDisplayName(instrument.patch);
  }
  if (instrument.kind === "wavetable") {
    return instrument.preset.name || "Wavetable";
  }
  if (instrument.kind === "sampler") {
    return instrument.preset.name || "Sampler";
  }
  if (instrument.kind === "externalMidi") {
    return `MIDI Ch ${instrument.patch.channel}`;
  }
  return instrument.preset.name || defaultSlotName(slot);
}
